/**
 * PortalSignModeViewer — D-Q2 read-only viewer for sign-mode reviews.
 *
 * Shows the document with existing annotations, a signer status header banner,
 * and an "Open my signing link" button if the captured-identity email matches
 * a signer. The portal is read-only for signing — annotation POSTs return 403.
 */
import { useQuery } from '@tanstack/react-query';
import { Loader2, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DocumentAnnotator } from '@/components/document-review/DocumentAnnotator';

interface PortalSignModeViewerProps {
  viewToken: string;
  reviewId: string;
  actorId: string;
  pdfUrl: string;
  reviewStatus?: string;
  apiBase?: string;
  onClose?: () => void;
}

interface SignerStatus {
  email: string;
  role?: string;
  position: number;
  signed_at?: string;
  is_current_actor: boolean;
}

function formatSignedAt(signed_at: string): string {
  try {
    return new Date(signed_at).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return signed_at;
  }
}

function SignerStatusBadge({ signer }: { signer: SignerStatus }) {
  if (signer.signed_at) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-700">
        <CheckCircle2 className="w-3 h-3" />
        {signer.email} signed {formatSignedAt(signer.signed_at)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-amber-700">
      <Clock className="w-3 h-3" />
      Waiting on {signer.email}
    </span>
  );
}

export function PortalSignModeViewer({
  viewToken,
  reviewId,
  actorId,
  pdfUrl,
  reviewStatus,
  apiBase,
  onClose,
}: PortalSignModeViewerProps) {
  const base = apiBase ?? (import.meta.env.VITE_API_URL as string) ?? '';

  const { data, isLoading, error } = useQuery({
    queryKey: ['portal-sign-view', viewToken, reviewId, actorId],
    queryFn: async () => {
      const res = await fetch(
        `${base}/api/portal/reviews/${reviewId}/sign-view?view_token=${encodeURIComponent(viewToken)}`,
        { headers: { 'X-Actor-Session': actorId } }
      );
      if (!res.ok) throw new Error(`Failed to load sign-view meta: ${res.status}`);
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="animate-spin w-6 h-6 text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-destructive">
        Unable to load signing status. Please refresh.
      </div>
    );
  }

  const signers: SignerStatus[] = data?.signers ?? [];
  const actorIsSigner: boolean = data?.actor_is_signer ?? false;
  // Prefer status from sign-view API response; fall back to prop passed from parent
  const effectiveReviewStatus: string | undefined = (data?.status as string | undefined) ?? reviewStatus;

  return (
    <div className="flex flex-col h-full">
      {/* Status header banner */}
      <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex flex-wrap items-center gap-4">
        <span className="text-xs font-medium text-blue-800 uppercase tracking-wide">
          Signing status
        </span>
        <div className="flex flex-wrap gap-3">
          {signers.map((s, i) => (
            <SignerStatusBadge key={i} signer={s} />
          ))}
        </div>
        {actorIsSigner && (
          <Button
            size="sm"
            variant="default"
            className="ml-auto text-xs"
            onClick={() => {
              // R4 signing-link redemption — trigger via email link, not inline
              alert(
                'Check your email for your personal signing link. The link was sent when this review was created.'
              );
            }}
          >
            Open my signing link
          </Button>
        )}
        {onClose && (
          <Button size="sm" variant="ghost" onClick={onClose} className="text-xs">
            Close
          </Button>
        )}
      </div>

      {/* Read-only document view — mode='sign' prevents annotation writes.
       *  Iter6 sweep C (F20): pass isPortal so the toolbar suppresses architect-
       *  side affordances (Export PDF, Send for Review). Combined with
       *  reviewStatus passthrough, viewOnly kicks in when the review is locked
       *  so even the annotation-mode buttons disappear. portalContext is
       *  required so the Download button can hit the token-gated file path. */}
      <div className="flex-1 overflow-auto">
        <DocumentAnnotator
          documentReviewId={reviewId}
          pdfUrl={pdfUrl}
          mode="sign"
          reviewStatus={effectiveReviewStatus}
          onClose={onClose}
          isPortal
          portalContext={{
            viewToken,
            actorId,
            inCanvasStampsEnabled: false,
          }}
        />
      </div>
    </div>
  );
}
